import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    LearningReportDetail,
    getLearningReport,
    markLearningReportRead,
} from '../services/learningReportService';
import {
    KNOWLEDGE_GLOSSARY,
    KNOWLEDGE_GLOSSARY_REGEX,
    KnowledgeGlossaryEntry,
    findGlossaryEntry,
} from '../constants/knowledgeGlossary';
import KnowledgePointModal from '../components/learning-report/KnowledgePointModal';
import { ClickableWord } from '../components/ClickableWord';
import { LoopyAvatar } from '../components/loopy';
import './LearningReportDetailPage.css';

type OpenEntry = (entry: KnowledgeGlossaryEntry) => void;

/**
 * Wrap English words (≥3 letters) in a plain-text fragment with ClickableWord
 * so each becomes a dictionary lookup trigger — same popover used site-wide.
 * The 3-letter floor skips A/B/C leftovers from CEFR markers like "A1" and
 * common function words ("go", "is", "on") that are rarely dictionary targets.
 */
const wrapDictionaryWords = (text: string, keyPrefix: string): React.ReactNode[] => {
    if (!text) return [];
    const regex = /\b[a-zA-Z]{3,}(?:['-][a-zA-Z]+)*\b/g;
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(text)) !== null) {
        if (m.index > lastIndex) nodes.push(text.slice(lastIndex, m.index));
        nodes.push(
            <ClickableWord key={`${keyPrefix}-${m.index}`} word={m[0]} />,
        );
        lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
    return nodes;
};

/**
 * Detect glossary pills inside a plain-text fragment. `withDict` controls
 * whether surviving (non-matched) English words become dictionary-clickable —
 * used only inside `**bold**` / `*italic*` spans so prose outside emphasis
 * stays visually calm.
 */
const linkifyGlossary = (
    text: string,
    onOpen: OpenEntry,
    keyPrefix: string,
    withDict: boolean,
): React.ReactNode[] => {
    if (!text) return [];
    const wrapPlain = (chunk: string, subKey: string): React.ReactNode[] =>
        withDict ? wrapDictionaryWords(chunk, subKey) : [chunk];
    const regex = new RegExp(KNOWLEDGE_GLOSSARY_REGEX.source, 'gi');
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    let idx = 0;
    while ((m = regex.exec(text)) !== null) {
        const start = m.index;
        if (start > lastIndex) {
            parts.push(...wrapPlain(text.slice(lastIndex, start), `${keyPrefix}-t-${start}`));
        }
        const matched = m[0];
        const entry = findGlossaryEntry(matched);
        if (entry) {
            parts.push(
                <button
                    key={`${keyPrefix}-${entry.key}-${idx++}`}
                    type="button"
                    className="learning-report-kp-pill"
                    onClick={() => onOpen(entry)}
                >
                    {matched}
                </button>,
            );
        } else {
            parts.push(matched);
        }
        lastIndex = start + matched.length;
    }
    if (lastIndex < text.length) {
        parts.push(...wrapPlain(text.slice(lastIndex), `${keyPrefix}-tail`));
    }
    return parts;
};

/**
 * Render inline markdown: `**bold**` → <strong>, `*italic*` → <em>.
 * Emphasis spans are extracted FIRST so that glossary terms landing inside a
 * `**…**` span (e.g. `**「觀光 (C2)」**`) still render as clickable pills —
 * and the surrounding `**` markers are correctly consumed instead of leaking
 * as literal asterisks. Glossary detection runs on each slice afterward.
 */
const linkifyText = (text: string, onOpen: OpenEntry, keyPrefix = 'inline'): React.ReactNode => {
    if (!text) return text;
    const regex = /\*\*([^*]+?)\*\*|\*([^*\s][^*]*?)\*/g;
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(text)) !== null) {
        if (m.index > lastIndex) {
            nodes.push(
                ...linkifyGlossary(text.slice(lastIndex, m.index), onOpen, `${keyPrefix}-t-${m.index}`, false),
            );
        }
        if (m[1] !== undefined) {
            nodes.push(
                <strong key={`${keyPrefix}-b-${m.index}`} className="learning-report-strong">
                    {linkifyGlossary(m[1], onOpen, `${keyPrefix}-b-${m.index}-i`, true)}
                </strong>,
            );
        } else if (m[2] !== undefined) {
            nodes.push(
                <em key={`${keyPrefix}-i-${m.index}`} className="learning-report-em">
                    {linkifyGlossary(m[2], onOpen, `${keyPrefix}-i-${m.index}-i`, true)}
                </em>,
            );
        }
        lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) {
        nodes.push(
            ...linkifyGlossary(text.slice(lastIndex), onOpen, `${keyPrefix}-tail`, false),
        );
    }
    return nodes;
};

const renderMarkdown = (md: string, onOpen: OpenEntry): React.ReactNode => {
    const nodes: React.ReactNode[] = [];
    const blocks = md.split(/\n{2,}/);
    blocks.forEach((block, i) => {
        const lines = block.split('\n');
        const first = lines[0].trim();
        if (first.startsWith('## ')) {
            nodes.push(
                <h2 key={i} className="learning-report-section-title">
                    {first.slice(3)}
                </h2>,
            );
            const rest = lines.slice(1).join('\n').trim();
            if (rest) nodes.push(<p key={`${i}-p`}>{linkifyText(rest, onOpen)}</p>);
        } else if (first.startsWith('# ')) {
            nodes.push(
                <h2 key={i} className="learning-report-section-title">
                    {first.slice(2)}
                </h2>,
            );
        } else {
            nodes.push(<p key={i}>{linkifyText(block, onOpen)}</p>);
        }
    });
    return nodes;
};

const LearningReportDetailPage: React.FC = () => {
    const { t, i18n } = useTranslation('learningReport');
    const { id } = useParams<{ id: string }>();
    const [report, setReport] = useState<LearningReportDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeEntry, setActiveEntry] = useState<KnowledgeGlossaryEntry | null>(null);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        getLearningReport(id)
            .then((r) => {
                if (cancelled) return;
                setReport(r);
                if (r.status === 'ready' && !r.readAt) {
                    markLearningReportRead(id).catch(() => {
                        // best-effort — not fatal
                    });
                }
            })
            .catch((err) => !cancelled && setError((err as Error).message))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [id]);

    /**
     * Deep-link handler: the weekly-report email renders glossary pills as
     * `<a href="/reports/{id}#kp-{key}">`. When the page loads with a matching
     * hash, auto-open the corresponding modal so the user lands right on the
     * explanation they clicked.
     *
     * Waits until `report` has loaded (body needs to render first, otherwise
     * there's a flash of empty page behind the modal). Re-runs on hash change
     * so in-page anchor clicks also work.
     */
    useEffect(() => {
        if (!report) return;
        const applyHash = () => {
            const hash = window.location.hash;
            if (!hash.startsWith('#kp-')) return;
            const key = hash.slice(4);
            const entry = KNOWLEDGE_GLOSSARY.find((e) => e.key === key);
            if (entry) setActiveEntry(entry);
        };
        applyHash();
        window.addEventListener('hashchange', applyHash);
        return () => window.removeEventListener('hashchange', applyHash);
    }, [report]);

    const formatDate = (iso: string | null | undefined) =>
        iso
            ? new Date(iso).toLocaleDateString(i18n.language, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
              })
            : '';

    const body = useMemo(
        () => (report?.aiContent ? renderMarkdown(report.aiContent, setActiveEntry) : null),
        [report?.aiContent],
    );

    if (loading) {
        return <div className="learning-report-detail-loading">{t('loading')}</div>;
    }
    if (error || !report) {
        return (
            <div className="learning-report-detail-error">
                <p>{error ?? t('notFound')}</p>
                <Link to="/reports">{t('backToList')}</Link>
            </div>
        );
    }

    return (
        <div className="learning-report-detail-page">
            <nav className="learning-report-detail-nav">
                <Link to="/reports">← {t('backToList')}</Link>
            </nav>
            <header className="learning-report-detail-header">
                <p className="learning-report-detail-period">
                    {formatDate(report.periodStart)} → {formatDate(report.periodEnd)}
                </p>
                <div className="learning-report-detail-title">
                    <LoopyAvatar size="sm" emotion="pencil" ariaLabel="Loopy" />
                    <h1>{t('detailTitle')}</h1>
                </div>
            </header>

            {report.status !== 'ready' && (
                <div className="learning-report-detail-notice">
                    {t(`status_${report.status}`)}
                </div>
            )}

            {body && <article className="learning-report-detail-body">{body}</article>}

            {report.recommendedCourseId && (
                <section className="learning-report-recommendation">
                    <h3>{t('recommendedCourse')}</h3>
                    <Link to={`/courses/${report.recommendedCourseId}`}>
                        {t('openRecommendedCourse')}
                    </Link>
                    {report.recommendationReason && (
                        <p className="learning-report-recommendation-reason">
                            {report.recommendationReason}
                        </p>
                    )}
                </section>
            )}

            {report.kind === 'trial_end' && (
                <section className="learning-report-trial-end-cta">
                    <Link
                        to="/pricing?source=trial_end_report"
                        className="learning-report-trial-end-cta__button"
                    >
                        {t('trialEndCta')}
                    </Link>
                    <p className="learning-report-trial-end-cta__sub">
                        {t('trialEndCtaSub')}
                    </p>
                </section>
            )}

            {activeEntry && (
                <KnowledgePointModal
                    entry={activeEntry}
                    onClose={() => setActiveEntry(null)}
                />
            )}
        </div>
    );
};

export default LearningReportDetailPage;
